import React, { useState, useEffect } from 'react';

export function EventCard({ event }) {
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const images = Array.isArray(event.images) ? event.images : [];

  const formattedDate = event.date
    ? new Date(event.date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';

  const isModalOpen = selectedImageIndex !== null;

  // Keyboard navigation
  useEffect(() => {
    if (!isModalOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setSelectedImageIndex(null);
      if (e.key === 'ArrowRight') {
        setSelectedImageIndex((prev) => (prev + 1) % images.length);
      }
      if (e.key === 'ArrowLeft') {
        setSelectedImageIndex((prev) => (prev - 1 + images.length) % images.length);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen, images.length]);

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl space-y-4 text-white">
      {/* 1. Date & Title */}
      <div>
        <span className="text-xs font-semibold text-blue-400 uppercase tracking-wider">
          {formattedDate}
        </span>
        <h2 className="text-2xl font-bold tracking-tight mt-1 text-white">
          {event.title}
        </h2>
      </div>

      {/* 2. Description */}
      <div
        className="text-slate-300 text-sm leading-relaxed"
        dangerouslySetInnerHTML={{ __html: event.description || '' }}
      />

      {/* 3. Image Grid */}
      {images.length > 0 && (
        <div
          className={`pt-2 ${
            images.length === 1
              ? 'flex justify-center'
              : 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3'
          }`}
        >
          {images.map((imgUrl, index) => (
            <div
              key={index}
              onClick={() => setSelectedImageIndex(index)}
              className={`relative rounded-xl overflow-hidden bg-black border border-slate-800 cursor-pointer ${
                images.length === 1 ? 'w-full max-w-xl mx-auto aspect-video' : 'w-full aspect-video'
              }`}
            >
              <img
                src={imgUrl}
                alt=""
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
              />
            </div>
          ))}
        </div>
      )}

      {/* 4. Fullscreen Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 flex flex-col justify-between items-center select-none"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.96)',
            zIndex: 99999,
          }}
          onClick={() => setSelectedImageIndex(null)}
        >
          {/* Top Close Button */}
          <div className="w-full flex justify-end p-4">
            <button
              type="button"
              onClick={() => setSelectedImageIndex(null)}
              className="text-white hover:text-red-400 text-3xl p-2 bg-transparent border-0 cursor-pointer"
            >
              &times;
            </button>
          </div>

          {/* Left Arrow */}
          {images.length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedImageIndex((prev) => (prev - 1 + images.length) % images.length);
              }}
              className="fixed left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white text-4xl p-3 bg-transparent border-0 cursor-pointer"
            >
              &#8249;
            </button>
          )}

          {/* Center Image */}
          <div
            className="w-full h-full max-h-[85vh] flex items-center justify-center px-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={images[selectedImageIndex]}
              alt=""
              className="max-w-full max-h-[85vh] object-contain shadow-2xl"
            />
          </div>

          {/* Right Arrow */}
          {images.length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedImageIndex((prev) => (prev + 1) % images.length);
              }}
              className="fixed right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white text-4xl p-3 bg-transparent border-0 cursor-pointer"
            >
              &#8250;
            </button>
          )}

          {/* Bottom Counter */}
          <div className="w-full flex justify-center pb-4 text-xs text-neutral-400 font-mono">
            {selectedImageIndex + 1} / {images.length}
          </div>
        </div>
      )}
    </div>
  );
}